import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UiModule } from 'src/app/ui/ui.module';
import { SessionUser } from 'src/app/models/session';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Frame } from 'src/app/models/session';
import { ScreenSizeService } from 'src/app/services/screen-size.service';
import { SearchBarSizeEnum } from 'src/app/ui/search-bar/search-bar.component';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { SpaceBoardFoldersService } from 'src/app/services/space-board-folders.service';
import { PresenceProvider } from 'src/app/services/presence-provider';
import { SpaceRepository } from 'src/app/state/space.repository';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Router } from '@angular/router';
import { RealtimeSpaceService } from 'src/app/services/realtime-space.service';
import { DomListenerFactoryService } from 'src/app/services/dom-listener-factory.service';
import { PencilButtonComponent } from '../../../standalones/components/pencil-button/pencil-button.component';
import { CommonComponentsModule } from '../../../common/common-components.module';
import {
  PencilDropDownButtonComponent,
  PencilDropdownButtonOption,
} from '../../../standalones/components/pencil-drop-down-button/pencil-drop-down-button.component';
import {
  ModalTemplateComponent,
  PencilModalSizeEnum,
} from '../../../standalones/components/modals/modal-template/modal-template.component';
import { ParticipantsSelectionListComponent } from '../participants-selection-list/participants-selection-list.component';

export enum ParticipantsOption {
  'ONLINE_PARTICIPANTS_ONLY' = 'ONLINE_PARTICIPANTS_ONLY',
  'ALL_SPACE_PARTICIPANTS' = 'ALL_SPACE_PARTICIPANTS',
}

@UntilDestroy()
@Component({
  selector: 'app-wb-create-private-board-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalTemplateComponent,
    PencilDropDownButtonComponent,
    CommonComponentsModule,
    PencilButtonComponent,
    TranslateModule,
    UiModule,
    ParticipantsSelectionListComponent,
  ],
  templateUrl: './wb-create-private-board-modal.component.html',
  styleUrls: ['./wb-create-private-board-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WbCreatePrivateBoardModalComponent implements OnInit, OnDestroy {
  sessionId: string;
  roomId: string;
  selectedFrame: Frame;
  createMultiplePrivateBoards: boolean;
  participantsCount: number;

  pencilModalSizeEnum = PencilModalSizeEnum;
  renderedAsMatBottomSheet = this.screenSizeService.renderMenuAsBottomSheet$;

  readonly MAX_BOARDS_CREATION_COUNT = 70;
  searchBarSizeEnum = SearchBarSizeEnum;
  activeParticipantsUsersIds = new Set<string>();
  IconTypes = IconTypes;
  ParticipantsOption = ParticipantsOption;
  createPrivateBoardsOptions: PencilDropdownButtonOption[] = [
    {
      id: ParticipantsOption.ONLINE_PARTICIPANTS_ONLY,
      value: this.translateService.instant('Online participants only'),
    },
    {
      id: ParticipantsOption.ALL_SPACE_PARTICIPANTS,
      value: this.translateService.instant('All Space participants'),
    },
  ];
  selectedOption?: PencilDropdownButtonOption = this.createPrivateBoardsOptions[0];
  selectedParticipantsOptionCount = 0;
  reachedMaxBoardsCreationCount = false;
  reachedMaxFoldersLimit = false;
  shouldDisableCreatePrivateBoards = false;
  selectedPrivateUsersForFrame: string[] = [];
  domListener = this.domListenerFactoryService.createInstance();

  @ViewChild('privateBoardPerParticipantModal') privateBoardPerParticipantModal?: ElementRef;

  constructor(
    @Optional() private dialogRef: MatDialogRef<WbCreatePrivateBoardModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      sessionId: string;
      roomId: string;
      frame: Frame;
      createMultiplePrivateBoards?: boolean;
      participants?: SessionUser[];
      isModalForDuplicateBoard?: boolean;
    },
    private screenSizeService: ScreenSizeService,
    private translateService: TranslateService,
    private spaceBoardFoldersService: SpaceBoardFoldersService,
    private presenceProvider: PresenceProvider,
    private spaceRepo: SpaceRepository,
    private router: Router,
    private realtimeSpaceService: RealtimeSpaceService,
    private domListenerFactoryService: DomListenerFactoryService,
  ) {
    this.sessionId = data.sessionId;
    this.roomId = data.roomId;
    this.selectedFrame = data.frame;
    this.createMultiplePrivateBoards = data.createMultiplePrivateBoards ?? false;
    this.participantsCount = data.participants?.length ?? 0;
  }

  ngOnInit(): void {
    this.setupEscKeyListener();
    this.router.events.pipe(untilDestroyed(this)).subscribe(() => this.dialogRef.close());
    this.listenToOnlineParticipantsCount();
    this.updateCreatePrivateBoardsModalState();
    this.handleDemotedUser();
  }

  onCreatePrivateBoardsSelectionChange(option: PencilDropdownButtonOption): void {
    this.selectedOption = option;
    if (option.id === ParticipantsOption.ONLINE_PARTICIPANTS_ONLY) {
      this.selectedParticipantsOptionCount = this.activeParticipantsUsersIds.size;
    } else {
      this.selectedParticipantsOptionCount = this.participantsCount ?? 0;
    }
    this.updateCreatePrivateBoardsModalState();
  }

  listenToOnlineParticipantsCount(): void {
    this.activeParticipantsUsersIds.clear();
    this.presenceProvider
      .getSpacePresenceActivity(this.sessionId)
      .pipe(untilDestroyed(this))
      .subscribe((activeUsersIds) => {
        // Create a new Set with only the participants that are active
        this.activeParticipantsUsersIds = new Set(
          this.data.participants
            ?.filter((participant) => activeUsersIds.has(participant._id))
            .map((participant) => participant._id),
        );
        if (this.activeParticipantsUsersIds.size === 0) {
          this.selectedParticipantsOptionCount = this.participantsCount ?? 0;
          this.selectedOption = this.createPrivateBoardsOptions[1];
        } else {
          this.selectedParticipantsOptionCount = this.activeParticipantsUsersIds.size;
          this.selectedOption = this.createPrivateBoardsOptions[0];
        }
        this.updateCreatePrivateBoardsModalState();
      });
  }

  handleConfirmCreateMultiplePrivateBoards(): void {
    if (
      this.selectedOption?.id === ParticipantsOption.ONLINE_PARTICIPANTS_ONLY &&
      this.data.participants
    ) {
      for (let i = this.data.participants.length - 1; i >= 0; i--) {
        if (!this.activeParticipantsUsersIds.has(this.data.participants[i]._id)) {
          this.data.participants.splice(i, 1);
        }
      }
    }
    this.close(true);
  }

  private isExceedingMaxBoardsLimit(): boolean {
    return (
      !!this.selectedParticipantsOptionCount &&
      this.selectedParticipantsOptionCount > this.MAX_BOARDS_CREATION_COUNT
    );
  }

  private isPrivateBoardsCreationDisabled(): boolean {
    return (
      this.reachedMaxBoardsCreationCount ||
      this.reachedMaxFoldersLimit ||
      !this.selectedParticipantsOptionCount
    );
  }

  private handleDemotedUser(): void {
    this.spaceRepo.isCurrentUserHost$.pipe(untilDestroyed(this)).subscribe((isHost) => {
      if (!isHost) {
        this.close();
      }
    });
  }

  private updateCreatePrivateBoardsModalState(): void {
    this.reachedMaxFoldersLimit = this.spaceBoardFoldersService.isBoardFoldersLimitReached();
    this.reachedMaxBoardsCreationCount = this.isExceedingMaxBoardsLimit();
    this.shouldDisableCreatePrivateBoards = this.isPrivateBoardsCreationDisabled();
  }

  handleConfirmCreatePrivateBoard(): void {
    this.saveSelectedFramePrivacyAndClose();
  }

  private saveSelectedFramePrivacyAndClose() {
    this.selectedFrame.privateUsers = this.selectedPrivateUsersForFrame;
    this.realtimeSpaceService.service.addFrame(this.selectedFrame, this.sessionId, this.roomId);
    this.close(true);
  }

  handleParticipantsSelectionChange(selectedParticipants: string[]): void {
    this.selectedPrivateUsersForFrame = selectedParticipants;
  }

  close(value?: any): void {
    this.dialogRef.close(value);
  }

  private setupEscKeyListener() {
    this.domListener.add(
      'document',
      'keyup.esc',
      () => {
        this.dialogRef.close();
      },
      true,
    );
  }

  ngOnDestroy(): void {
    this.domListener.clear();
  }
}
